// useTableColumnReorder.js

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { updateView } from 'services/views/ViewWriteService';
import { canPersistViewLayout } from 'views/utils/organicViews';

const normalizeKeyList = (keys) => (
    Array.isArray(keys) ? keys.filter((key) => key && typeof key === 'string') : []
);

const serializeKeyList = (keys) => JSON.stringify(keys || []);

const useTableColumnReorder = ({ view, visibleFieldKeys, resetKey }) => {
    const normalizedKeys = useMemo(() => (
        normalizeKeyList(visibleFieldKeys)
    ), [visibleFieldKeys]);
    const normalizedSignature = useMemo(() => (
        serializeKeyList(normalizedKeys)
    ), [normalizedKeys]);

    const [orderedFieldKeys, setOrderedFieldKeys] = useState(normalizedKeys);
    const [draggingKey, setDraggingKey] = useState('');
    const [dragOverKey, setDragOverKey] = useState('');
    const lastNonEmptyKeysRef = useRef(normalizedKeys);
    const viewKeyRef = useRef(resetKey || view?.key || '');

    useEffect(() => {
        const currentViewKey = resetKey || view?.key || '';
        if (viewKeyRef.current !== currentViewKey) {
            viewKeyRef.current = currentViewKey;
            lastNonEmptyKeysRef.current = normalizedKeys;
            setOrderedFieldKeys(normalizedKeys);
            return;
        }

        if (normalizedKeys.length) {
            lastNonEmptyKeysRef.current = normalizedKeys;
        }
    }, [normalizedKeys, resetKey, view?.key]);

    useEffect(() => {
        if (!normalizedKeys.length && lastNonEmptyKeysRef.current?.length) return;
        setOrderedFieldKeys(normalizedKeys);
    }, [normalizedSignature, normalizedKeys]);

    const handleDragEnd = useCallback(() => {
        setDraggingKey('');
        setDragOverKey('');
    }, []);

    const commitOrder = useCallback(async (nextKeys, previousKeys) => {
        if (!canPersistViewLayout(view)) return;

        const viewKey = view?.key;
        if (!viewKey) return;

        try {
            await updateView(viewKey, {
                properties: nextKeys.length ? nextKeys : null,
            });
        } catch (error) {
            console.error('[TableView] Column reorder failed:', error);
            setOrderedFieldKeys(previousKeys);
        }
    }, [view]);

    const handleDragStart = useCallback((columnKey, event) => {
        if (!columnKey) return;
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', columnKey);
        setDraggingKey(columnKey);
    }, []);

    const handleDragOver = useCallback((columnKey, event) => {
        if (!draggingKey || !columnKey || columnKey === draggingKey) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDragOverKey(columnKey);
    }, [draggingKey]);

    const handleDrop = useCallback(async (columnKey, event) => {
        if (!draggingKey || !columnKey) return;
        event.preventDefault();

        if (columnKey === draggingKey) {
            handleDragEnd();
            return;
        }

        const fromIndex = orderedFieldKeys.indexOf(draggingKey);
        const toIndex = orderedFieldKeys.indexOf(columnKey);

        if (fromIndex < 0 || toIndex < 0) {
            handleDragEnd();
            return;
        }

        const nextOrder = [...orderedFieldKeys];
        const [moved] = nextOrder.splice(fromIndex, 1);
        nextOrder.splice(toIndex, 0, moved);

        const previousOrder = orderedFieldKeys;
        setOrderedFieldKeys(nextOrder);
        handleDragEnd();

        await commitOrder(nextOrder, previousOrder);
    }, [commitOrder, draggingKey, handleDragEnd, orderedFieldKeys]);

    return {
        orderedFieldKeys,
        draggingKey,
        dragOverKey,
        handleDragStart,
        handleDragOver,
        handleDrop,
        handleDragEnd,
    };
};

export default useTableColumnReorder;
